import React from 'react';
import { Helmet } from 'react-helmet-async';
import { business } from '../data/realContent';

const SITE_URL = business.url;
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

const V2SEO = ({
  title,
  description,
  path = '/',
  keywords,
  ogImage = DEFAULT_OG,
  ogType = 'website',
  faqs,
  serviceSchema,
  articleSchema,
  noindex = false,
}) => {
  const fullTitle = title.includes('Cosmik Drones') ? title : `${title} | Cosmik Drones`;
  const canonical = `${SITE_URL}${path}`;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#localbusiness`,
    name: business.name,
    url: SITE_URL,
    telephone: business.phone,
    email: business.email,
    image: ogImageUrl,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: business.city,
      addressRegion: business.region,
      postalCode: business.postalCode,
      addressCountry: business.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: business.lat,
      longitude: business.lon,
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Lehigh Valley' },
      { '@type': 'State', name: 'Pennsylvania' },
      {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: business.lat,
          longitude: business.lon,
        },
        geoRadius: '200000',
      },
    ],
    memberOf: {
      '@type': 'Organization',
      name: business.chamberMember,
      url: business.chamberUrl,
    },
    sameAs: Object.values(business.social),
  };

  const faqSchema =
    faqs && faqs.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: f.a,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content="Cosmik Drones" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      <meta name="geo.region" content="US-PA" />
      <meta name="geo.placename" content="Lehigh Valley" />
      <meta name="geo.position" content={`${business.lat};${business.lon}`} />
      <meta name="ICBM" content={`${business.lat}, ${business.lon}`} />

      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default V2SEO;
